import { get } from '@ember/object';
import Component from '@glimmer/component';
import { type FunctionBasedModifier, modifier } from 'ember-modifier';

import { moveArrayItem } from '../utils/array.ts';

export type DropZoneModifier = FunctionBasedModifier<{
  Args: { Positional: [number]; Named: object };
  Element: HTMLElement;
}>;
export type DragModifier<T> = FunctionBasedModifier<{
  Args: { Positional: [T, number]; Named: object };
  Element: HTMLElement;
}>;

export interface DragToSortSignature<T> {
  Args: {
    items: T[];
    updateItems?: (items: T[]) => void;
  };
  Blocks: {
    item: [DropZoneModifier, DragModifier<T>, T, number];
  };
}

export class DragToSort<T> extends Component<DragToSortSignature<T>> {
  draggedItem: T | null = null;
  draggedIndex: number | null = null;
  dropTargetIndex: number | null = null;

  reorderItems = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) {
      return;
    }

    const items = this.args.items;
    if (!items) {
      return;
    }

    const reorderedItems = moveArrayItem(items, fromIndex, toIndex);

    // Clear existing items and add reordered ones
    if (this.args.updateItems) {
      this.args.updateItems(reorderedItems);
    } else {
      items.splice(0, items.length, ...reorderedItems);
    }
  };

  // Drag modifier for draggable items
  dragModifier = modifier(
    (element: HTMLElement, [item, index]: [T, number]) => {
      const handleDragStart = (event: DragEvent) => {
        if (!event.dataTransfer) {
          return;
        }

        this.draggedItem = item;
        this.draggedIndex = index;

        // Set drag data
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/html', element.outerHTML);

        // Add visual feedback
        element.classList.add('dragging');
        element.style.opacity = '0.5';
      };

      const handleDragEnd = () => {
        this.draggedItem = null;
        this.draggedIndex = null;
        this.dropTargetIndex = null;

        // Remove visual feedback
        element.classList.remove('dragging');
        element.style.opacity = '';

        // Remove drop zone indicators from all items
        const allItems = element.parentElement?.querySelectorAll('.drag-item');
        allItems?.forEach((item) => {
          item.classList.remove('drop-zone-above', 'drop-zone-below');
        });
      };

      // Make the element draggable
      element.draggable = true;
      element.classList.add('drag-item');

      // Add event listeners
      element.addEventListener('dragstart', handleDragStart);
      element.addEventListener('dragend', handleDragEnd);

      // Cleanup function
      return () => {
        element.removeEventListener('dragstart', handleDragStart);
        element.removeEventListener('dragend', handleDragEnd);
      };
    }
  );

  // Drop zone modifier for drop targets
  dropZoneModifier = modifier((element: HTMLElement, [index]: [number]) => {
    const handleDragOver = (event: DragEvent) => {
      event.preventDefault();
      if (!event.dataTransfer) {
        return;
      }

      if (this.draggedIndex === null) {
        return;
      }

      event.dataTransfer.dropEffect = 'move';

      // Determine drop position based on mouse position
      const rect = element.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      const isAbove = event.clientY < midY;

      // Update drop target
      if (index === this.draggedIndex) {
        this.dropTargetIndex = index;
      } else if (index < this.draggedIndex) {
        this.dropTargetIndex = isAbove ? index : index + 1;
      } else {
        this.dropTargetIndex = isAbove ? index - 1 : index;
      }

      // Add visual indicators
      element.classList.remove('drop-zone-above', 'drop-zone-below');
      if (index === this.draggedIndex) {
        element.classList.add('drop-zone-above', 'drop-zone-below');
      } else {
        element.classList.add(isAbove ? 'drop-zone-above' : 'drop-zone-below');
      }
    };

    const handleDragLeave = () => {
      element.classList.remove('drop-zone-above', 'drop-zone-below');
    };

    const handleDrop = (event: DragEvent) => {
      event.preventDefault();

      if (this.draggedIndex !== null && this.dropTargetIndex !== null) {
        this.reorderItems(this.draggedIndex, this.dropTargetIndex);
      }

      // Clean up visual indicators
      element.classList.remove('drop-zone-above', 'drop-zone-below');
    };

    // Add event listeners
    element.addEventListener('dragover', handleDragOver);
    element.addEventListener('dragleave', handleDragLeave);
    element.addEventListener('drop', handleDrop);

    // Cleanup function
    return () => {
      element.removeEventListener('dragover', handleDragOver);
      element.removeEventListener('dragleave', handleDragLeave);
      element.removeEventListener('drop', handleDrop);
    };
  });

  <template>
    {{#each @items as |_ index|}}
      {{yield
        this.dropZoneModifier
        this.dragModifier
        (get @items index)
        index
        to="item"
      }}
    {{/each}}
  </template>
}
